/**
 * „Standards": wie ein neuer Server, wie eine Instanz üblicherweise
 * eingerichtet wird — zum Nachschlagen, ein Abschnitt je Akkordeon.
 *
 * Die Texte pflegen Administratoren hier im Modal (Knopf „Bearbeiten").
 * Alle anderen sehen nur die Abschnitte.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, BookOpen, Pencil, Plus, Trash2, X } from 'lucide-react';
import { standards, type StandardBereich, type StandardEintrag } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { ChangelogInhalt } from './ChangelogAnsicht';

const TITEL: Record<StandardBereich, string> = {
  server: 'Standards für Server',
  instanz: 'Standards für Instanzen',
};

export default function StandardsKnopf({ bereich }: { bereich: StandardBereich }) {
  const [offen, setOffen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOffen(true)}
        title="Wie wird das üblicherweise eingerichtet?"
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-surface px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
      >
        <BookOpen size={15} /> Standards
      </button>
      {offen && <StandardsModal bereich={bereich} onClose={() => setOffen(false)} />}
    </>
  );
}

function StandardsModal({ bereich, onClose }: { bereich: StandardBereich; onClose: () => void }) {
  const { user } = useAuth();
  const istAdmin = user?.role === 'ADMIN';
  const [bearbeiten, setBearbeiten] = useState(false);
  const qc = useQueryClient();
  const schluessel = ['standards', bereich];
  const { data: eintraege = [], isLoading, error } = useQuery({
    queryKey: schluessel,
    queryFn: () => standards.list(bereich),
  });
  const neuLaden = () => qc.invalidateQueries({ queryKey: schluessel });

  const ordnen = useMutation({
    mutationFn: (ids: string[]) => standards.order(bereich, ids),
    onSuccess: (liste) => qc.setQueryData(schluessel, liste),
  });
  const verschieben = (index: number, richtung: -1 | 1) => {
    const ids = eintraege.map((e) => e.id);
    const ziel = index + richtung;
    if (ziel < 0 || ziel >= ids.length) return;
    [ids[index], ids[ziel]] = [ids[ziel], ids[index]];
    ordnen.mutate(ids);
  };

  const [neu, setNeu] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-xl bg-surface shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="standards-titel"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
          <div>
            <h2 id="standards-titel" className="text-base font-semibold text-slate-900">{TITEL[bereich]}</h2>
            <p className="mt-1 text-sm text-slate-500">So wird es bei uns üblicherweise gemacht. Ein Abschnitt je Thema.</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {istAdmin && (
              <button
                type="button"
                onClick={() => { setBearbeiten((b) => !b); setNeu(false); }}
                className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium ${
                  bearbeiten ? 'bg-brand-50 text-brand-700' : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                <Pencil size={13} /> {bearbeiten ? 'Fertig' : 'Bearbeiten'}
              </button>
            )}
            <button type="button" onClick={onClose} aria-label="Schließen" className="text-slate-400 hover:text-slate-600">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="space-y-2 overflow-y-auto px-6 py-5">
          {isLoading && <p className="text-sm text-slate-400">Lädt …</p>}
          {error && <p className="text-sm text-red-600">Die Standards ließen sich nicht laden.</p>}
          {eintraege.map((e, i) =>
            bearbeiten ? (
              <EintragBearbeiten
                key={e.id}
                eintrag={e}
                oben={i > 0 ? () => verschieben(i, -1) : undefined}
                unten={i < eintraege.length - 1 ? () => verschieben(i, 1) : undefined}
                gespeichert={neuLaden}
              />
            ) : (
              <details key={e.id} className="group rounded-md border border-slate-200 bg-surface px-4 py-2.5" open={eintraege.length === 1}>
                <summary className="cursor-pointer select-none text-sm font-medium text-slate-800 marker:text-slate-400">
                  {e.titel}
                </summary>
                <div className="pb-1">
                  <ChangelogInhalt text={e.inhalt} />
                </div>
              </details>
            ),
          )}
          {!isLoading && !error && eintraege.length === 0 && (
            <p className="text-sm text-slate-400">Noch keine Einträge.</p>
          )}
          {bearbeiten && (neu ? (
            <EintragFormular
              bereich={bereich}
              onFertig={() => { setNeu(false); neuLaden(); }}
              onAbbrechen={() => setNeu(false)}
            />
          ) : (
            <button
              type="button"
              onClick={() => setNeu(true)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 hover:underline"
            >
              <Plus size={15} /> Abschnitt hinzufügen
            </button>
          ))}
        </div>

        <div className="flex shrink-0 justify-end border-t border-slate-200 px-6 py-4">
          <button type="button" onClick={onClose} className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-700">
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}

function EintragBearbeiten({
  eintrag, oben, unten, gespeichert,
}: {
  eintrag: StandardEintrag;
  oben?: () => void;
  unten?: () => void;
  gespeichert: () => void;
}) {
  const [offen, setOffen] = useState(false);
  const loeschen = useMutation({ mutationFn: () => standards.remove(eintrag.id), onSuccess: gespeichert });

  if (offen) {
    return <EintragFormular eintrag={eintrag} onFertig={() => { setOffen(false); gespeichert(); }} onAbbrechen={() => setOffen(false)} />;
  }
  return (
    <div className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2">
      <span className="flex-1 truncate text-sm text-slate-800">{eintrag.titel}</span>
      <button type="button" onClick={oben} disabled={!oben} aria-label="Nach oben" className="rounded p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30">
        <ArrowUp size={15} />
      </button>
      <button type="button" onClick={unten} disabled={!unten} aria-label="Nach unten" className="rounded p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30">
        <ArrowDown size={15} />
      </button>
      <button type="button" onClick={() => setOffen(true)} aria-label="Bearbeiten" className="rounded p-1 text-slate-400 hover:text-brand-700">
        <Pencil size={15} />
      </button>
      <button
        type="button"
        onClick={() => { if (window.confirm(`„${eintrag.titel}" löschen?`)) loeschen.mutate(); }}
        aria-label="Löschen"
        className="rounded p-1 text-slate-400 hover:text-red-600"
      >
        <Trash2 size={15} />
      </button>
    </div>
  );
}

/** Neuer Abschnitt (mit `bereich`) oder bestehender (mit `eintrag`). */
function EintragFormular({
  bereich, eintrag, onFertig, onAbbrechen,
}: {
  bereich?: StandardBereich;
  eintrag?: StandardEintrag;
  onFertig: () => void;
  onAbbrechen: () => void;
}) {
  const [titel, setTitel] = useState(eintrag?.titel ?? '');
  const [inhalt, setInhalt] = useState(eintrag?.inhalt ?? '');
  const speichern = useMutation({
    mutationFn: () => (eintrag ? standards.update(eintrag.id, { titel, inhalt }) : standards.create(bereich!, { titel, inhalt })),
    onSuccess: onFertig,
  });
  return (
    <div className="space-y-2 rounded-md border border-brand-200 bg-brand-50/40 p-3">
      <input
        value={titel}
        onChange={(e) => setTitel(e.target.value)}
        placeholder="Titel, z. B. „Neuer Server bei Hostinger“"
        className="w-full rounded-md border border-slate-300 bg-surface px-3 py-1.5 text-sm"
        autoFocus
      />
      <textarea
        value={inhalt}
        onChange={(e) => setInhalt(e.target.value)}
        rows={8}
        placeholder={'- **Betriebssystem:** Ubuntu 24.04 LTS\n- **Standort:** Deutschland'}
        className="w-full rounded-md border border-slate-300 bg-surface px-3 py-2 font-mono text-xs"
      />
      <p className="text-xs text-slate-500">Zeilen mit „- “ werden Aufzählungen, **so** wird fett, Leerzeilen trennen Absätze.</p>
      {eintrag?.geaendertVon && (
        <p className="text-xs text-slate-400">Zuletzt geändert von {eintrag.geaendertVon}, {new Date(eintrag.updatedAt).toLocaleString('de-DE')}</p>
      )}
      {speichern.isError && <p className="text-xs text-red-600">Speichern hat nicht geklappt.</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onAbbrechen} className="rounded-md px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100">
          Abbrechen
        </button>
        <button
          type="button"
          onClick={() => speichern.mutate()}
          disabled={!titel.trim() || speichern.isPending}
          className="rounded-md bg-brand-600 px-3 py-1.5 text-sm text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {speichern.isPending ? 'Speichert …' : 'Speichern'}
        </button>
      </div>
    </div>
  );
}
